import Immutable from 'seamless-immutable'
import { createAction, handleActions } from 'redux-actions'
import { v4 as uuid } from 'uuid'

import fetch from '../../../utils/fetch'
import { AppThunk } from '..'
import { GlobalScene, Scene } from './Scene'
import { SavedScene } from './SavedScene'
import { Temp, Comment, TempTags } from './Temp'
import { getScenesList, getPendingExternalRename, getPendingExternalDelete, getPendingExternalUpdate } from './selectors'
import { getIsDirty } from '../dirty/selectors'
import { setDirty, clearDirty } from '../dirty'
import * as fromUndoRedo from '../undo-redo'
import _keyBy from 'lodash/keyBy'
import _omit from 'lodash/omit'
import _isEmpty from 'lodash/isEmpty'
import { getCurrent } from '../users/selectors'
import { LEFT_PANEL_TAB } from '../../../components/visualizer/panels/panel-left'
import { setActiveLeftPanelTab } from '../threeviewer/ui'
import * as actionTypes from '../action-types'
import * as fromThreeviewerFiles from '../threeviewer/files'
import * as fromTemplates from '../templates'
import * as fromCombinations from '../combinations'
import { loadCombination } from '../combinations/actions/load-combination'
import { getChangedModels } from '../combinations/actions/get-changed-models'
import DEFAULT_INTERACTIONS from '../combinations/interactions'
import { getNodeList } from '../threeviewer/selectors'
import { HttpError } from '../../../../types/HttpError'

export const share = createAction('scenes/SHARE')
export const unshare = createAction('scenes/UNSHARE')
export const receiveScenes = createAction('scenes/RECEIVE')
export const updateScenes = createAction('scenes/UPDATE')
export const removeScene = createAction('scenes/REMOVE_ID')
export const error = createAction('scenes/ERROR')
export const clearError = createAction('scenes/CLEAR_LOAD_STATE')
export const receiveUpdate = createAction('scenes/RECEIVE_UPDATE')
const setCurrent = createAction('scenes/SET_CURRENT')
export const setTempTitle = createAction('scenes/SET_TEMP_TITLE')
export const setTempTags = createAction('scenes/SET_TEMP_TAGS')
export const setTempTagsGroup = createAction('scenes/SET_TEMP_TAGS_GROUP')
export const clearTempTags = createAction('scenes/CLEAR_TEMP_TAGS')
export const setTempSelectedCombination = createAction('scenes/SET_TEMP_COMBINATION')
export const addTempComment = createAction('scenes/ADD_TEMP_COMMENT')
export const updateTempComment = createAction('scenes/UPDATE_TEMP_COMMENT')
export const removeTempComment = createAction('scenes/REMOVE_TEMP_COMMENT')
export const clearTempComments = createAction('scenes/CLEAR_TEMP_COMMENTS')
export const setTempComments = createAction('scenes/SET_TEMP_COMMENTS')
export const updateGlobalScenes = createAction('scenes/UPDATE_GLOBAL')
export const statusTempComment = createAction('scenes/STATUS_TEMP_COMMENT')
export const setSaveSceneId = createAction('scenes/SET_SAVE_SCENE_ID')
export const setScenesList = createAction('scenes/SET_SCENES_LIST')
export const setPendingExternalUpdate = createAction<SavedScene | null>(actionTypes.SCENE_SET_PENDING_EXTERNAL_UPDATE)
export const setPendingExternalRename = createAction<SavedScene | null>(actionTypes.SCENE_SET_PENDING_EXTERNAL_RENAME)
export const setPendingExternalDelete = createAction<{ id: string } | null>(actionTypes.SCENE_SET_PENDING_EXTERNAL_DELETE)
export const setShowExternalRenameNotification = createAction<boolean>(actionTypes.SCENE_SET_SHOW_EXTERNAL_RENAME_NOTIFICATION)

export function mergeSceneIntoList (nextScene: SavedScene) : AppThunk {
  return (dispatch, getState) => {
    const currentScenesList = getScenesList(getState())
    let updatedScenesList : SavedScene[]
    if (currentScenesList.find((scene) => scene.id === nextScene.id)) {
      updatedScenesList = currentScenesList.map((scene) => scene.id === nextScene.id ? nextScene : scene)
    } else {
      updatedScenesList = [...currentScenesList, nextScene]
    }
    dispatch(setScenesList(updatedScenesList))
  }
}

export function removeSceneFromList (sceneIdToRemove: string) : AppThunk {
  return (dispatch, getState) => {
    const currentScenesList = getScenesList(getState())
    const updatedScenesList = currentScenesList.filter((scene) => scene.id !== sceneIdToRemove)
    dispatch(setScenesList(updatedScenesList))
  }
}

const DEFAULT_TEMPLATE_COMPONENT_IDS = ['floorBox', 'wallLeft', 'wallRight', 'skirtingLeft', 'skirtingRight']

function withDefaultTemplateVisibility (modifications: { [key: string]: any } = {}) {
  const normalized = { ...modifications }
  DEFAULT_TEMPLATE_COMPONENT_IDS.forEach((id) => {
    const existing = normalized[id] || {}
    if (typeof existing.visible === 'undefined') {
      normalized[id] = {
        ...existing,
        visible: false,
      }
    }
  })
  return normalized
}

export function getScene (id: string, setAsCurrent = false): AppThunk {
  return async (dispatch) => {
    const res = await fetch(`/api/scenes/${id}`)
    const json = await res.json()
    dispatch(receiveScenes([json]))
    if (setAsCurrent) {
      dispatch(setCurrent(id))
      dispatch(setTempTitle({ title: null }))
    }
  }
}

export function fetchGlobalScenes (): AppThunk {
  return (dispatch) => {
    return fetch('/api/scenes/global')
      .then(res => {
        return res.json()
      }).then(res => {
        dispatch(receiveScenes(res))
      })
      .catch(() => dispatch(error('Error when fetching the scenes')))
  }
}

export function fetchUserScenes (): AppThunk {
  return (dispatch) => {
    return fetch('/api/scenes/user')
      .then(res => {
        return res.json()
      }).then(res => {
        dispatch(receiveScenes(res))
      })
      .catch(() => dispatch(error('Error when fetching the scenes')))
  }
}

export const shareScene = (payload: { id: string, title: string }): AppThunk => {
  return (dispatch) => {
    return fetch('/api/scenes', {
      method: 'PUT',
      body: JSON.stringify({ combinationId: payload.id, title: payload.title, global: false })
    })
      .then((res) => res.json())
      .then(json => dispatch(updateScenes(json)))
      .catch(() => dispatch(error('Error when creating scene')))
  }
}

export const unShareScene = (payload: { id: string, removeAsAdmin?: boolean }): AppThunk => {
  return (dispatch) => {
    return fetch(`/api/scenes/${payload.id}`, {
      method: 'DELETE',
      body: JSON.stringify({ id: payload.id })
    })
      .then((res) => res.json())
      .then(() => dispatch(removeScene({ id: payload.id, removeAsAdmin: payload.removeAsAdmin }))
      )
      .catch(() => dispatch(error('Error when removing scene')))
  }
}

export const updateScene = (payload: { id: string, title: string, tags?: string[], comments?: any[], global: boolean }): AppThunk => {
  return (dispatch) => {
    return fetch(`/api/scenes/${payload.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ title: payload.title, tags: payload.tags, comments: payload.comments, global: payload.global })
    })
      .then((res) => res.json())
      .then(json => dispatch(updateScenes(json)))
      .catch(() => dispatch(error('Error when updating scene')))
  }
}

export const saveGlobalScene = (payload: { id: string, title: string, tags: number[], comments: any[], global?: boolean }): AppThunk => {
  return (dispatch) => {
    return fetch(`/api/scenes/${payload.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ title: payload.title, tags: payload.tags, comments: payload.comments, global: payload.global ?? false })
    })
      .then((res) => res.json())
      .then(json => dispatch(updateGlobalScenes(json)))
      .catch(() => dispatch(error('Error when updating scene')))
  }
}

function buildSavedScenePayload (
  state: any,
  currentModels: { [id: string]: any },
  currentTemplateModels: { [id: string]: any },
  currentRoomsetModels: { [id: string]: any },
  customHomeAppearanceAssignments: { [id: string]: any }
) {
  const viewer = state.threeviewer.viewer
  const CUSTOM_HOME_TEMPLATE_ID = 'custom-home'
  const activeTemplateId = state.templates.activeId ?? null
  const roomsetIdFromState = state.roomsets.currentId ?? null
  const customHomePanelState = state.threeviewer?.customHome?.scenePanelState
  const currentCombination = state.combinations.entries.getIn([state.combinations.currentId || ''])

  const hasCustomHomeSchema = !!viewer?.roomManager?.schema
  const hasCustomHomeAppearanceAssignments = Object.keys(customHomeAppearanceAssignments).length > 0
  const isCustomHome = customHomePanelState === 'CUSTOM_HOME' || hasCustomHomeSchema || hasCustomHomeAppearanceAssignments

  const inferredRoomsetId = roomsetIdFromState || (
    !isCustomHome &&
      activeTemplateId &&
      activeTemplateId !== 'default' &&
      activeTemplateId !== CUSTOM_HOME_TEMPLATE_ID
      ? activeTemplateId
      : null
  )

  const templateId = isCustomHome
    ? CUSTOM_HOME_TEMPLATE_ID
    : (activeTemplateId ?? inferredRoomsetId ?? 'default')

  return {
    projectId: state.projects.currentId ?? null,
    cameraSettings: viewer?.cameraManager?.getCameraSettings() ?? null,
    models: currentModels,
    roomsetId: isCustomHome ? null : inferredRoomsetId,
    roomsetModels: isCustomHome ? {} : currentRoomsetModels,
    templateId,
    templateModels: currentTemplateModels,
    customHomeSchema: isCustomHome
      ? (viewer?.roomManager?.schema ?? currentCombination?.customHomeSchema ?? null)
      : null,
    customHomeAppearanceAssignments: isCustomHome ? customHomeAppearanceAssignments : {},
  }
}

export function saveOrUpdateScene (savedSceneId: string | null, title?: string): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState()
    const isDirty = getIsDirty(state)
    if (savedSceneId !== null && !isDirty) {
      return
    }
    const allCurrentModels = dispatch(getChangedModels()) as { [key: string]: any }
    const currentModels: { [key: string]: any } = {}
    const currentTemplateModels: { [key: string]: any } = {}
    const currentRoomsetModels: { [key: string]: any } = {}

    Object.entries(allCurrentModels).forEach(([key, m]) => {
      const modelType = (m as any).modelType
      if (modelType === 'template') {
        // Template loader looks up modifications by model name (e.g. "floor", "wallLeft"),
        // not by Three.js UUID.  Re-key by name so getTemplateModelSettings can find them.
        const name: string = (m as any).name || key
        currentTemplateModels[name] = m
      } else if (modelType === 'roomset') {
        currentRoomsetModels[key] = m
      } else {
        currentModels[key] = m
      }
    })

    const viewer = state.threeviewer.viewer
    const customHomeAppearanceAssignments: { [id: string]: any } = {}
    if (viewer?.roomManager?.roomObject?.parent) {
      const roomChanges = dispatch(getChangedModels(
        new Set([viewer.roomManager.roomObject])
      )) as { [key: string]: any }
      Object.values(roomChanges).forEach((model: any) =>
        model.parts?.forEach((p: any) => {
          if (p.kvadratMeshId) customHomeAppearanceAssignments[p.kvadratMeshId] = p
        })
      )
    }

    const scenePayload = buildSavedScenePayload(state, currentModels, currentTemplateModels, currentRoomsetModels, customHomeAppearanceAssignments)
    const thumbnailBase64 = generateThumbnail(viewer)
    const currentScenesList = getScenesList(state)
    const existingScene = savedSceneId
      ? currentScenesList.find((scene) => scene.id === savedSceneId)
      : null

    const savedTitle = title?.trim() || existingScene?.title || `Version ${new Date().toISOString().slice(0, 10)}`
    const currentUser = getCurrent(state)

    const res = await window.fetch('/api/savedscenes', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        ...scenePayload,
        title: savedTitle,
        id: savedSceneId || undefined,
        thumbnailBase64,
      })
    })

    if (!res.ok) {
      throw new HttpError('Failed to save scene', res.status)
    }

    const json = await res.json()
    const thumbnailUrl = json?.thumbnail?.key
      ? `/api/storage/get/${json.thumbnail.key}.jpg`
      : null
    const nextEntry: SavedScene = {
      id: json.id,
      title: json.title,
      thumbnail: thumbnailUrl,
      updatedBy: json.updatedBy || 'Unknown user',
      updatedByName: json.updatedByName || currentUser?.name || currentUser?.email || 'Unknown user',
      updatedAt: json.updatedAt || new Date().toISOString(),
    }

    dispatch(mergeSceneIntoList(nextEntry))
    dispatch(setSaveSceneId(json.id))
    dispatch(setTempTitle({ title: json.title || savedTitle }))
    dispatch(clearDirty())
    dispatch(setActiveLeftPanelTab(LEFT_PANEL_TAB.SAVED))
  }
}

export function fetchScenesByProject (projectId: string): AppThunk<Promise<void>> {
  return async (dispatch) => {
    const res = await window.fetch(`/api/savedscenes/byProject/${projectId}`)
    if (!res.ok) {
      throw new HttpError('Failed to load scenes', res.status)
    }
    const scenes: Array<{ id: string, title: string, thumbnail?: { key?: string } | null, updatedAt: string, updatedBy: string, updatedByName?: string }> = await res.json()
    const mapped: SavedScene[] = scenes.map((scene) => ({
      id: scene.id,
      title: scene.title,
      thumbnail: scene.thumbnail?.key ? `/api/storage/get/${scene.thumbnail.key}.jpg` : null,
      updatedAt: scene.updatedAt,
      updatedBy: scene.updatedBy,
      updatedByName: scene.updatedByName,
    }))
    dispatch(setScenesList(mapped))
  }
}

function clearLoadedSceneCombinationRoots (): AppThunk {
  return (dispatch, getState) => {
    const state = getState()
    const viewer = state.threeviewer.viewer

    if (!viewer) {
      return
    }

    const rootNodes = Object.values(getNodeList(state)).filter((node: any) => (
      node &&
      node.parent === viewer.scene &&
      node.userData?.isCombination
    ))

    rootNodes.forEach((node: any) => {
      dispatch(fromCombinations.removeModel(node, false))
    })

    dispatch(fromThreeviewerFiles.dispose())
  }
}

export function applyPendingExternalChanges () : AppThunk {
  return (dispatch, getState) => {
    const pendingDelete = getPendingExternalDelete(getState())
    if (pendingDelete) {
      dispatch(removeSceneFromList(pendingDelete.id))
      dispatch(setPendingExternalDelete(null))
    }

    const pendingRename = getPendingExternalRename(getState())
    if (pendingRename) {
      dispatch(mergeSceneIntoList(pendingRename))
      dispatch(setPendingExternalRename(null))
    }

    const pendingUpdate = getPendingExternalUpdate(getState())
    if (pendingUpdate) {
      dispatch(mergeSceneIntoList(pendingUpdate))
      dispatch(setPendingExternalUpdate(null))
    }
  }
}

export function fetchAndLoadSavedScene (savedSceneId: string): AppThunk<Promise<void>> {
  return async (dispatch) => {
    // Clear any stale scene error from a previous load attempt so it does not
    // immediately re-trigger the error modal when isLoading becomes true.
    dispatch(clearError())

    dispatch(clearLoadedSceneCombinationRoots())

    const instanceId = uuid()
    dispatch(fromThreeviewerFiles.load('combination', instanceId))

    const res = await window.fetch(`/api/savedscenes/${savedSceneId}`)
    if (!res.ok) {
      throw new HttpError('Failed to load scene', res.status)
    }
    const scene = await res.json()

    const templateId = scene.roomsetId || scene.templateId || 'default'
    const isDefaultTemplateScene = !scene.roomsetId && (scene.templateId === 'default' || !scene.templateId)
    const templateModels = isDefaultTemplateScene
      ? withDefaultTemplateVisibility(scene.templateModels || {})
      : (scene.templateModels || {})
    const modifications = _isEmpty(scene.roomsetModels) ? templateModels : scene.roomsetModels

    if (templateId && templateId !== 'custom-home') {
      await dispatch(fromTemplates.load(templateId, {
        modifications,
        forceReload: true,
        setCamera: false
      }))
    }

    dispatch(loadCombination({
      instanceId,
      combination: scene,
      isComplementary: false,
      interactions: DEFAULT_INTERACTIONS,
    }))

    dispatch(fromThreeviewerFiles.loaded('combination', instanceId))
    dispatch(setSaveSceneId(savedSceneId))
    dispatch(setTempTitle({ title: scene.title }))
    dispatch(fromUndoRedo.dispose() as any)
    dispatch(clearDirty())
  }
}

export function resetCurrentSavedSceneToUnsaved (): AppThunk {
  return (dispatch) => {
    dispatch(setDirty())
    dispatch(setSaveSceneId(null))
    dispatch(setTempTitle({ title: null }))
  }
}

export function deleteSavedScene (sceneId: string): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState()
    const scenesList = getScenesList(state)
    const currentSavedSceneId = state.scenes.saveSceneId

    const res = await window.fetch(`/api/savedscenes/${sceneId}`, {
      method: 'DELETE'
    })

    if (!res.ok) {
      throw new HttpError('Failed to delete scene', res.status)
    }

    dispatch(setScenesList(scenesList.filter((scene) => scene.id !== sceneId)))

    if (sceneId === currentSavedSceneId) {
      dispatch(resetCurrentSavedSceneToUnsaved())
    }
  }
}

export function renameSavedScene (sceneId: string, title: string): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState()
    const currentSavedSceneId = state.scenes.saveSceneId

    const res = await window.fetch(`/api/savedscenes/rename/${sceneId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        title,
      })
    })

    if (!res.ok) {
      throw new HttpError('Failed to rename scene', res.status)
    }

    const updatedScenesList = getScenesList(getState()).map((savedScene) => {
      if (savedScene.id !== sceneId) {
        return savedScene
      }

      return {
        ...savedScene,
        title,
      }
    })

    dispatch(setScenesList(updatedScenesList))

    if (sceneId === currentSavedSceneId) {
      dispatch(setTempTitle({ title }))
    }
  }
}

const initialState = Immutable<{
  entries: { [id: string]: Scene },
  globalEntries: { [id: string]: GlobalScene },
  error: string | null
  currentId: string | null
  scenesList: SavedScene[]
  saveSceneId: string | null
  temporary: Temp,
  pendingExternalUpdate: SavedScene | null
  pendingExternalRename: SavedScene | null
  pendingExternalDelete: { id: string } | null
  showExternalRenameNotification: boolean
}>({
  entries: {},
  globalEntries: {},
  error: null,
  currentId: null,
  scenesList: [] as SavedScene[],
  saveSceneId: null,
  temporary:
    {
      title: null,
      comments: [] as Comment[],
      tags: {} as TempTags
    },
  pendingExternalUpdate: null,
  pendingExternalRename: null,
  pendingExternalDelete: null,
  showExternalRenameNotification: false,
})

type State = typeof initialState

function generateThumbnail (viewer: any): string | null {
  try {
    if (!viewer || !viewer.canvasImageCapturer) {
      return null
    }
    return viewer.canvasImageCapturer.generateBase64FromCanvas()
  } catch {
    return null
  }
}

export default handleActions<State, any>({
  [setCurrent.toString()]: (state, action) => state.setIn(['currentId'], action.payload),
  [setSaveSceneId.toString()]: (state, action) => state.setIn(['saveSceneId'], action.payload),
  [setScenesList.toString()]: (state, { payload }) => state.setIn(['scenesList'], payload),
  [setPendingExternalUpdate.toString()]: (state, { payload }) => state.setIn(['pendingExternalUpdate'], payload),
  [setPendingExternalRename.toString()]: (state, { payload }) => state.setIn(['pendingExternalRename'], payload),
  [setPendingExternalDelete.toString()]: (state, { payload }) => state.setIn(['pendingExternalDelete'], payload),
  [setShowExternalRenameNotification.toString()]: (state, { payload }) => state.setIn(['showExternalRenameNotification'], payload),
  [clearError.toString()]: (state) => state.setIn(['error'], null),
  [receiveScenes.toString()]: (state, { payload }) => {
    const userScenesChanged = payload.userScenes
    const globalScenes = payload.globalScenes
    const newState = {
      error: null,
      entries: userScenesChanged ? _keyBy(payload.userScenes, 'id') : state.entries,
      globalEntries: globalScenes ? _keyBy(payload.globalScenes, 'id') : state.globalEntries
    }
    return state.merge(newState)
  },
  [updateScenes.toString()]: (state, { payload }) => {
    return state
      .setIn(['entries', payload.id], payload)
      .setIn(['error'], null)
  },
  [updateGlobalScenes.toString()]: (state, { payload }) => {
    return state
      .setIn(['entries', payload.id], payload)
      .setIn(['globalEntries', payload.id], payload)
      .setIn(['error'], null)
  },
  [removeScene.toString()]: (state, { payload }) => {
    if (payload.removeAsAdmin) state.setIn(['globalEntries'], _omit(state.globalEntries, [payload.id]))
    return state.setIn(['entries'], _omit(state.entries, [payload.id]))
  },
  [error.toString()]: (state, { payload }) => state.merge({ error: payload }),
  [receiveUpdate.toString()]: (state, { payload }) => {
    const parameter = payload.global ? 'globalEntries' : 'entries'
    if (payload.removedAt) return state.setIn([parameter], _omit(state[parameter], [payload.id]))
    return state
      .setIn([parameter, payload.id], payload)
      .setIn(['error'], null)
  },
  [setTempTitle.toString()]: (state, { payload }) => {
    return state
      .setIn(['temporary', 'title'], payload.title)
  },
  [setTempSelectedCombination.toString()]: (state, { payload }) => {
    return state
      .setIn(['temporary', 'combination'], payload.combination)
  },
  [addTempComment.toString()]: (state, { payload } : { payload: Comment }) => {
    return state.setIn(['temporary', 'comments'], state.temporary.comments.concat([payload]))
  },
  [updateTempComment.toString()]: (state, { payload } : { payload: { id: string, body: Comment } }) => {
    return state.setIn(['temporary', 'comments', payload.id], payload.body)
  },
  [removeTempComment.toString()]: (state, { payload }) => {
    const comment = state.getIn(['temporary', 'comments']).filter(comment => comment.id === payload)[0]
    // if comment has anchor we need to update the other comments with anchors index
    if (comment.index) {
      const allComments = state.getIn(['temporary', 'comments']).filter(comment => comment.id !== payload)
      let index = comment.index
      const updatedComments = allComments.map(c => {
        if (!c.index || c.index < index) return c
        index++
        return c.setIn(['index'], (index - 1))
      })
      return state.setIn(['temporary', 'comments'], updatedComments)
    }
    return state.setIn(['temporary', 'comments'], state.temporary.comments.filter(comment => comment.id !== payload))
  },
  [clearTempComments.toString()]: (state) => {
    return state.setIn(['temporary', 'comments'], [])
  },
  [setTempComments.toString()]: (state, { payload }) => {
    return state.setIn(['temporary', 'comments'], payload)
  },
  [setTempTags.toString()]: (state, { payload }: { payload: {
    key: string,
    tags: any[]
  }}) => {
    return state
      .setIn(['temporary', 'tags', payload.key], payload.tags)
  },
  [setTempTagsGroup.toString()]: (state, { payload }: { payload: any}) => {
    return state
      .setIn(['temporary', 'tags'], payload)
  },
  [clearTempTags.toString()]: (state) => {
    return state
      .setIn(['temporary', 'tags'], {})
  },
  [statusTempComment.toString()]: (state, { payload }: {payload: any}) => {
    return state.setIn(['temporary', 'comments', payload.id, 'hasTempAnchor'], payload.hasTempAnchor)
  }
}, initialState)
